import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

// Angular Material Imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';

import { Conversation } from '../../../services/conversation';

@Component({
  selector: 'app-outcome-rate',
  standalone: true,
  imports: [
    CommonModule, FormsModule, BaseChartDirective, MatFormFieldModule,
    MatInputModule, MatDatepickerModule, MatNativeDateModule,
    MatButtonModule, MatIconModule, MatTableModule, MatCardModule, MatTabsModule
  ],
  providers: [DecimalPipe, DatePipe],
  templateUrl: './outcome-rate.component.html',
  styleUrls: ['../category-distribution/category-distribution.component.scss'] // Reusing your existing styles
})
export class OutcomeRateComponent implements OnInit {

  @Input() globalStartDate!: Date;
@Input() globalEndDate!: Date;
  rateTable: any[] = [];
  startDate = new Date();
   endDate = new Date();

  // Summary Stats
  totalSatisfied = 0;
  totalNotSatisfied = 0;
  averageRate = 0;

  /** Chart with 3 Lines: 2 Counts (Right Axis) and 1 Percentage (Left Axis) */
  lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Satisfaction Rate (%)',
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'yPercentage'
      },
      {
        data: [],
        label: 'Satisfied (Count)',
        borderColor: '#10b981',
        backgroundColor: 'transparent',
        borderDash: [3, 3],
        tension: 0.4,
        yAxisID: 'yCount'
      },
      {
        data: [],
        label: 'Not Satisfied (Count)',
        borderColor: '#ef4444',
        backgroundColor: 'transparent',
        borderDash: [3, 3],
        tension: 0.4,
        yAxisID: 'yCount'
      }
    ]
  };

  lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yPercentage: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        max: 100,
        title: { display: true, text: 'Rate (%)' }
      },
      yCount: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Volume' }
      }
    }
  };

  constructor(private conversationService: Conversation) {
    this.startDate.setDate(this.endDate.getDate() - 14);
  }

  ngOnInit(): void {
    this.fetchOutcomeRate();
  }

  ngOnChanges(changes: SimpleChanges) {
  if (changes['globalStartDate'] || changes['globalEndDate']) {
    this.startDate = this.globalStartDate;
    this.endDate = this.globalEndDate;
    this.fetchOutcomeRate(); // Your existing fetch/load function
  }
}

  fetchOutcomeRate(): void {
    const start = this.startDate.toISOString().split('T')[0];
    const end = this.endDate.toISOString().split('T')[0];

    this.conversationService.getOutcomeRate(start, end).subscribe(response => {
      const data = response.data ?? [];
      this.rateTable = data;
      this.calculateSummary(data);

      this.lineChartData = {
        labels: data.map((row: any) => row.date),
        datasets: [
          { ...this.lineChartData.datasets[0], data: data.map((row: any) => row.satisfaction_rate) },
          { ...this.lineChartData.datasets[1], data: data.map((row: any) => row.Satisfied) },
          { ...this.lineChartData.datasets[2], data: data.map((row: any) => row.Not_Satisfied) }
        ]
      };
    });
  }

  private calculateSummary(data: any[]): void {
    let s = 0, ns = 0;
    data.forEach(row => {
      s += (row.Satisfied || 0);
      ns += (row.Not_Satisfied || 0);
    });
    this.totalSatisfied = s;
    this.totalNotSatisfied = ns;
    this.averageRate = (s + ns) > 0 ? (s / (s + ns)) * 100 : 0;
  }

  downloadCSV(): void {
    if (this.rateTable.length === 0) return;

    const headers = ['Date', 'Satisfied', 'Not_Satisfied', 'Total', 'Satisfaction_Rate_%'];
    const csvRows = [
      headers.join(','),
      ...this.rateTable.map(row => [
        row.date,
        row.Satisfied,
        row.Not_Satisfied,
        row.total,
        row.satisfaction_rate.toFixed(2)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `outcome_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}